import React, { createContext, useContext, useState, useCallback } from 'react';
import { TransitionConfig, TransitionTheme, TransitionConfigContextType } from './shared-types';

// Default themes
const defaultThemes: Record<string, TransitionTheme> = {
  'default': {
    name: 'default',
    enter: {
      duration: 300,
      easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
    },
    exit: {
      duration: 250,
      easing: 'ease-out',
    },
    resize: {
      duration: 200,
      easing: 'ease-in-out',
    }
  },
  'smooth': {
    name: 'smooth',
    enter: {
      duration: 400,
      easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
    exit: {
      duration: 300,
      easing: 'cubic-bezier(0.47, 0, 0.745, 0.715)',
    },
    resize: {
      duration: 350,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    }
  },
  'quick': {
    name: 'quick',
    enter: {
      duration: 150,
      easing: 'cubic-bezier(0.19, 1, 0.22, 1)',
    },
    exit: {
      duration: 120,
      easing: 'ease-out',
    },
    resize: {
      duration: 100,
      easing: 'ease-in-out',
    }
  }
};

// Create the context with undefined default value
const TransitionConfigContext = createContext<TransitionConfigContextType | undefined>(undefined);

// Provider component
export function TransitionConfigProvider({ children }: { children: React.ReactNode }) {
  const [currentTheme, setCurrentTheme] = useState('default');
  const [themes, setThemes] = useState(defaultThemes);
  const [globalDuration, setGlobalDuration] = useState(300);
  const [globalEasing, setGlobalEasing] = useState('ease');
  
  // Get transition config
  const getTransitionConfig = useCallback((type: 'enter' | 'exit' | 'resize', panelId?: string): TransitionConfig => {
    const theme = themes[currentTheme];
    if (!theme) return { duration: globalDuration, easing: globalEasing };
    
    const baseConfig = theme[type];
    
    // Apply global overrides if no specific duration or easing
    return {
      duration: baseConfig.duration || globalDuration,
      easing: baseConfig.easing || globalEasing,
      delay: baseConfig.delay || 0
    };
  }, [currentTheme, themes, globalDuration, globalEasing]);
  
  // Get transition string (for CSS)
  const getTransitionString = useCallback((type: 'enter' | 'exit' | 'resize', panelId?: string): string => {
    const config = getTransitionConfig(type, panelId);
    return `transform ${config.duration}ms ${config.easing}, opacity ${config.duration}ms ${config.easing}`;
  }, [getTransitionConfig]);
  
  // Add a new theme
  const addTheme = useCallback((theme: TransitionTheme) => {
    setThemes(prev => ({
      ...prev,
      [theme.name]: theme
    }));
  }, []);
  
  // Context value
  const contextValue: TransitionConfigContextType = {
    currentTheme,
    themes,
    globalDuration,
    globalEasing,
    getTransitionConfig,
    getTransitionString,
    setCurrentTheme,
    setGlobalDuration,
    setGlobalEasing,
    addTheme
  };
  
  return (
    <TransitionConfigContext.Provider value={contextValue}>
      {children}
    </TransitionConfigContext.Provider>
  );
}

// Hook to use the context
export function useTransitionConfig() {
  const context = useContext(TransitionConfigContext);
  if (context === undefined) {
    throw new Error('useTransitionConfig must be used within a TransitionConfigProvider');
  }
  return context;
}